/**
 * Robust camera bootstrap for the QR Scanner module.
 * - Automatically triggers the permission prompt
 * - Prefers the rear camera, falls back to front, then facingMode constraints
 * - Classifies failures: permission denied / no camera / HTTPS required / in use
 */
import { Html5Qrcode } from "html5-qrcode";

/** Map any camera failure to a stable error code. */
export function classifyCameraError(e) {
  if (typeof window !== "undefined" && !window.isSecureContext) return "https";
  const name = e?.name || "";
  const msg = String(e?.message || e || "").toLowerCase();
  if (name === "NotAllowedError" || name === "PermissionDeniedError" ||
      msg.includes("permission") || msg.includes("notallowed") || msg.includes("denied")) return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError" ||
      msg.includes("requested device not found") || msg.includes("no camera") ||
      msg.includes("notfound") || msg.includes("device not found")) return "nocamera";
  if (name === "NotReadableError" || name === "AbortError" || name === "TrackStartError" ||
      msg.includes("in use") || msg.includes("notreadable") || msg.includes("trackstart") ||
      msg.includes("could not start video source") || msg.includes("hardware")) return "inuse";
  return "generic";
}

/** i18n key per error code (qr.cameraBlocked is the generic fallback). */
export const CAMERA_ERROR_KEYS = {
  denied: "qr.errDenied",
  nocamera: "qr.errNoCamera",
  https: "qr.errHttps",
  inuse: "qr.errInUse",
  generic: "qr.cameraBlocked",
};

/** Explicitly request camera permission (used by the "Allow Camera" button). */
export async function requestCameraPermission() {
  const stream = await navigator.mediaDevices.getUserMedia({ video: true });
  stream.getTracks().forEach((tr) => tr.stop());
}

/**
 * Start scanning inside #regionId. Tries: rear camera by device id → remaining
 * cameras → facingMode environment → facingMode user. Returns the Html5Qrcode
 * instance on success; throws the last error on total failure.
 */
export async function startQrScanner(regionId, onDecoded, { fps = 10, qrbox = 260 } = {}) {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    const e = new Error("HTTPS required");
    e.name = "SecurityError";
    throw e;
  }
  const scanner = new Html5Qrcode(regionId);
  const config = { fps, qrbox: { width: qrbox, height: qrbox } };
  let lastErr = null;

  // 1) Enumerate devices (this triggers the permission prompt automatically).
  try {
    const cams = await Html5Qrcode.getCameras();
    if (cams && cams.length) {
      const rear = cams.find((c) => /back|rear|environment/i.test(c.label || ""));
      // Rear cameras are usually listed last when labels are unavailable.
      const ordered = rear ? [rear, ...cams.filter((c) => c.id !== rear.id)] : [...cams].reverse();
      for (const cam of ordered) {
        try {
          await scanner.start(cam.id, config, onDecoded, () => {});
          return scanner;
        } catch (e) { lastErr = e; }
      }
    } else {
      lastErr = Object.assign(new Error("no camera"), { name: "NotFoundError" });
    }
  } catch (e) { lastErr = e; }

  // 2) Constraint fallback — rear first, then front.
  for (const facingMode of ["environment", "user"]) {
    try {
      await scanner.start({ facingMode }, config, onDecoded, () => {});
      return scanner;
    } catch (e) { lastErr = e; }
  }

  try { scanner.clear(); } catch (e) { console.debug("qr clear", e); }
  throw lastErr || new Error("camera failed");
}
